(() => {
  // COMPONENT: FreeGiftPromotions - evaluates repeatable merchant-defined free-gift rules and keeps the cart aligned across page, drawer, and cart template flows.
  const selectors = {
    config: '[data-free-gift-promotions-config]',
    checkout: 'button[name="checkout"], input[name="checkout"]',
    message: '[data-free-gift-message]',
  };

  const propertyKeys = {
    autoAdded: '_sk_free_gift_auto',
    ruleId: '_sk_free_gift_rule',
  };

  let rules = [];
  let latestCartState = null;
  let syncTimer = null;
  let syncInFlight = false;
  let rerunQueued = false;
  let syncPending = false;
  let dispatchingPromoCartEvents = false;

  const publicApi = window.SteakKingFreeGiftPromotions || {};
  window.SteakKingFreeGiftPromotions = publicApi;

  publicApi.isSyncPending = () => syncPending;

  const setSyncPending = (isPending) => {
    if (syncPending === isPending) return;

    syncPending = isPending;

    document.dispatchEvent(
      new CustomEvent('sk:free-gift:sync-state', {
        detail: {
          pending: syncPending,
        },
        bubbles: true,
      })
    );
  };

  const normalizeProductHandle = (value) =>
    String(value || '')
      .toLowerCase()
      .trim();

  const blockCheckoutDuringSync = (event) => {
    if (!syncPending) return;

    const checkoutTrigger = event.target && event.target.closest(selectors.checkout);
    if (!checkoutTrigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const blockCheckoutSubmitDuringSync = (event) => {
    if (!syncPending || !event.submitter || !event.submitter.matches(selectors.checkout)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const getRootUrl = () => {
    const configuredRoot = window.theme && window.theme.routes ? window.theme.routes.root_url : '/';
    return configuredRoot || '/';
  };

  const getCartUrl = () => `${getRootUrl()}cart.js`;
  const getCartAddUrl = () =>
    (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || `${getRootUrl()}cart/add.js`;
  const getCartUpdateUrl = () => `${getRootUrl()}cart/update.js`;

  // DATA: This theme currently renders promotion rules from more than one section instance, so the engine merges every config payload instead of trusting only the first node.
  const readConfig = () => {
    const configNodes = Array.prototype.slice.call(document.querySelectorAll(selectors.config));
    if (!configNodes.length) {
      rules = [];
      renderPromoMessage(latestCartState);
      return;
    }

    try {
      const ruleMap = new Map();

      configNodes.forEach((configNode) => {
        const parsedConfig = JSON.parse(configNode.textContent || '{}');
        const sectionId = String((parsedConfig && parsedConfig.sectionId) || '').trim();
        const parsedRules = Array.isArray(parsedConfig && parsedConfig.rules) ? parsedConfig.rules : [];

        parsedRules
          .map((rule) => normalizeRule(rule, sectionId))
          .filter((rule) => rule && rule.enabled && rule.qualifyingProductId && rule.giftVariantId)
          .forEach((rule) => {
            if (!ruleMap.has(rule.ruleKey)) {
              ruleMap.set(rule.ruleKey, rule);
            }
          });
      });

      rules = Array.from(ruleMap.values());
    } catch (error) {
      rules = [];
    }

    renderPromoMessage(latestCartState);
  };

  const buildRuleKey = (rule) => {
    return [
      normalizeProductHandle(rule.qualifyingProductHandle) || 'no-handle',
      parseInt(rule.qualifyingProductId, 10) || 0,
      parseInt(rule.requiredQuantity, 10) || 0,
      normalizeProductHandle(rule.giftProductHandle) || 'no-gift-handle',
      parseInt(rule.giftProductId, 10) || 0,
      parseInt(rule.giftVariantId, 10) || 0,
      parseInt(rule.giftQuantity, 10) || 0,
      rule.allowMultiples ? 'multi' : 'single',
    ].join('::');
  };

  const normalizeRule = (rule, sectionId) => {
    if (!rule || typeof rule !== 'object') return null;

    const requiredQuantity = Math.max(parseInt(rule.requiredQuantity, 10) || 0, 1);
    const giftQuantity = Math.max(parseInt(rule.giftQuantity, 10) || 0, 1);
    const qualifyingProductId = parseInt(rule.qualifyingProductId, 10) || 0;
    const giftVariantId = parseInt(rule.giftVariantId, 10) || 0;
    const giftProductId = parseInt(rule.giftProductId, 10) || 0;
    const allowMultiples = Boolean(rule.allowMultiples);

    if (!qualifyingProductId || !giftVariantId) return null;

    const normalizedRule = {
      blockId: String(rule.blockId || ''),
      sectionId: String(sectionId || ''),
      enabled: Boolean(rule.enabled),
      ruleName: String(rule.ruleName || '').trim(),
      qualifyingProductHandle: normalizeProductHandle(rule.qualifyingProductHandle),
      qualifyingProductId,
      qualifyingProductTitle: String(rule.qualifyingProductTitle || '').trim(),
      requiredQuantity,
      giftProductHandle: normalizeProductHandle(rule.giftProductHandle),
      giftProductId,
      giftProductTitle: String(rule.giftProductTitle || '').trim(),
      giftVariantId,
      giftVariantAvailable: Boolean(rule.giftVariantAvailable),
      giftQuantity,
      allowMultiples,
      lockedMessage: String(rule.lockedMessage || '').trim(),
      unlockedMessage: String(rule.unlockedMessage || '').trim(),
    };

    normalizedRule.ruleKey = buildRuleKey(normalizedRule);
    return normalizedRule;
  };

  const fetchCart = () =>
    fetch(getCartUrl(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(handleJsonResponse);

  const postJson = (url, payload) =>
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(handleJsonResponse);

  const handleJsonResponse = (response) => {
    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  };

  const getLineProperty = (item, key) => {
    if (!item || !item.properties || typeof item.properties !== 'object') return '';
    return String(item.properties[key] || '');
  };

  const isAutoGiftLine = (item) =>
    getLineProperty(item, propertyKeys.autoAdded) === 'true' || getLineProperty(item, propertyKeys.ruleId) !== '';

  const isRuleGiftLine = (item, rule) =>
    item.variant_id === rule.giftVariantId &&
    getLineProperty(item, propertyKeys.autoAdded) === 'true' &&
    getLineProperty(item, propertyKeys.ruleId) === rule.ruleKey;

  const isManagedGiftVariant = (item) => {
    if (!item) return false;

    return rules.some((rule) => item.variant_id === rule.giftVariantId);
  };

  // RULE: Gift-card-only carts should not show physical-product promotion prompts.
  const hasShippingRequiredItems = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return true;

    return cart.items.some((item) => item && item.requires_shipping !== false && !isAutoGiftLine(item));
  };

  const getUnauthorizedGiftLines = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => {
      const coveredByActiveRule = rules.some((rule) => isRuleGiftLine(item, rule));

      if (isAutoGiftLine(item)) {
        return !coveredByActiveRule;
      }

      return isManagedGiftVariant(item);
    });
  };

  const getQualifyingQuantity = (cart, rule) => {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (rule.qualifyingProductHandle) {
        if (normalizeProductHandle(item.handle) !== rule.qualifyingProductHandle) return total;
      } else if (item.product_id !== rule.qualifyingProductId) {
        return total;
      }

      if (isAutoGiftLine(item)) return total;
      return total + (parseInt(item.quantity, 10) || 0);
    }, 0);
  };

  const getGiftLinesForRule = (cart, rule) => {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => isRuleGiftLine(item, rule));
  };

  const getGiftLineQuantity = (giftLines) =>
    giftLines.reduce((total, item) => total + (parseInt(item.quantity, 10) || 0), 0);

  const getTargetGiftQuantity = (qualifyingQuantity, rule) => {
    if (!rule.giftVariantAvailable || qualifyingQuantity < rule.requiredQuantity) return 0;

    if (!rule.allowMultiples) {
      return rule.giftQuantity;
    }

    return Math.floor(qualifyingQuantity / rule.requiredQuantity) * rule.giftQuantity;
  };

  const getRuleStatus = (cart, rule) => {
    const qualifyingQuantity = getQualifyingQuantity(cart, rule);
    const giftLines = getGiftLinesForRule(cart, rule);
    const existingGiftQuantity = getGiftLineQuantity(giftLines);
    const targetGiftQuantity = getTargetGiftQuantity(qualifyingQuantity, rule);
    const remainingQuantity =
      qualifyingQuantity >= rule.requiredQuantity ? 0 : rule.requiredQuantity - qualifyingQuantity;

    return {
      rule,
      qualifyingQuantity,
      giftLines,
      existingGiftQuantity,
      targetGiftQuantity,
      remainingQuantity,
      isEligible: targetGiftQuantity > 0,
    };
  };

  const getGiftProperties = (rule) => ({
    [propertyKeys.autoAdded]: 'true',
    [propertyKeys.ruleId]: rule.ruleKey,
  });

  const collectGiftLineUpdates = (lineUpdates, giftLines, targetQuantity) => {
    let quantityToKeep = targetQuantity;

    giftLines.forEach((line) => {
      const lineQuantity = parseInt(line.quantity, 10) || 0;
      const nextQuantity = Math.max(Math.min(lineQuantity, quantityToKeep), 0);
      quantityToKeep = Math.max(quantityToKeep - nextQuantity, 0);

      if (nextQuantity !== lineQuantity) {
        lineUpdates.set(line.key, nextQuantity);
      }
    });
  };

  const postLineUpdates = (lineUpdates) => {
    if (!lineUpdates.size) return Promise.resolve(null);

    const updates = {};
    lineUpdates.forEach((quantity, lineKey) => {
      updates[lineKey] = quantity;
    });

    return postJson(getCartUpdateUrl(), { updates });
  };

  const postGiftAdditions = (giftAdditions) => {
    if (!giftAdditions.length) return Promise.resolve(null);

    return postJson(getCartAddUrl(), {
      items: giftAdditions,
    });
  };

  const commitGiftOperations = (lineUpdates, giftAdditions) => {
    const operations = [];

    if (lineUpdates.size) {
      operations.push(() => postLineUpdates(lineUpdates));
    }

    if (giftAdditions.length) {
      operations.push(() => postGiftAdditions(giftAdditions));
    }

    if (!operations.length) {
      return Promise.resolve(false);
    }

    return operations.reduce((chain, operation) => chain.then(operation), Promise.resolve()).then(() => true);
  };

  const applyRuleOperations = (cart) => {
    if (!cart || !Array.isArray(cart.items)) {
      return Promise.resolve(false);
    }

    const lineUpdates = new Map();
    const giftAdditions = [];
    const unauthorizedGiftLines = getUnauthorizedGiftLines(cart);

    unauthorizedGiftLines.forEach((line) => {
      lineUpdates.set(line.key, 0);
    });

    if (!rules.length) {
      return commitGiftOperations(lineUpdates, giftAdditions);
    }

    rules.forEach((rule) => {
      const status = getRuleStatus(cart, rule);

      if (status.targetGiftQuantity > status.existingGiftQuantity) {
        giftAdditions.push({
          id: rule.giftVariantId,
          quantity: status.targetGiftQuantity - status.existingGiftQuantity,
          properties: getGiftProperties(rule),
        });
      }

      if (status.targetGiftQuantity < status.existingGiftQuantity) {
        collectGiftLineUpdates(lineUpdates, status.giftLines, status.targetGiftQuantity);
      }
    });

    return commitGiftOperations(lineUpdates, giftAdditions);
  };

  const dispatchCartEvents = (cart) => {
    dispatchingPromoCartEvents = true;

    try {
      document.dispatchEvent(
        new CustomEvent('theme:cart:change', {
          detail: {
            cart,
            source: 'free-gift-promotions',
          },
          bubbles: true,
        })
      );

      document.dispatchEvent(
        new CustomEvent('theme:cart:reload', {
          detail: {
            source: 'free-gift-promotions',
          },
          bubbles: true,
        })
      );
    } finally {
      dispatchingPromoCartEvents = false;
    }
  };

  const applyMessageState = (container, state) => {
    if (!container) return;

    if (!state || !state.text) {
      container.textContent = '';
      container.hidden = true;
      container.classList.add('is-hidden');
      container.classList.remove('is-success');
      return;
    }

    container.textContent = state.text;
    container.hidden = false;
    container.classList.remove('is-hidden');
    container.classList.toggle('is-success', state.type === 'success');
  };

  const formatRuleMessage = (template, status) => {
    return template
      .replace(/\|\|product\|\|/g, status.rule.qualifyingProductTitle)
      .replace(/\|\|gift\|\|/g, status.rule.giftProductTitle)
      .replace(/\|\|required\|\|/g, String(status.rule.requiredQuantity))
      .replace(/\|\|remaining\|\|/g, String(status.remainingQuantity))
      .replace(/\|\|gift_quantity\|\|/g, String(status.rule.giftQuantity));
  };

  const getMessageState = (cart) => {
    if (!cart || !rules.length) return null;
    if (!hasShippingRequiredItems(cart)) return null;

    const statuses = rules.map((rule) => getRuleStatus(cart, rule));
    const eligibleStatus = statuses.find((status) => status.isEligible && status.rule.unlockedMessage);

    if (eligibleStatus) {
      return {
        text: formatRuleMessage(eligibleStatus.rule.unlockedMessage, eligibleStatus),
        type: 'success',
      };
    }

    const pendingStatus = statuses.find(
      (status) => !status.isEligible && status.remainingQuantity > 0 && status.rule.lockedMessage
    );

    if (!pendingStatus) return null;

    return {
      text: formatRuleMessage(pendingStatus.rule.lockedMessage, pendingStatus),
      type: 'info',
    };
  };

  const renderPromoMessage = (cart) => {
    const state = getMessageState(cart);
    document.querySelectorAll(selectors.message).forEach((container) => {
      applyMessageState(container, state);
    });
  };

  const runSync = () => {
    syncTimer = null;

    if (syncInFlight) {
      rerunQueued = true;
      setSyncPending(true);
      return;
    }

    syncInFlight = true;
    setSyncPending(true);

    Promise.resolve(latestCartState || fetchCart())
      .then((cart) => {
        latestCartState = cart;
        return applyRuleOperations(cart).then((cartChanged) => {
          if (!cartChanged) {
            renderPromoMessage(cart);
            return cart;
          }

          return fetchCart().then((updatedCart) => {
            latestCartState = updatedCart;
            renderPromoMessage(updatedCart);
            dispatchCartEvents(updatedCart);
            return updatedCart;
          });
        });
      })
      .catch(() => {
        renderPromoMessage(latestCartState);
      })
      .finally(() => {
        syncInFlight = false;

        if (rerunQueued) {
          rerunQueued = false;
          scheduleSync();
          return;
        }

        setSyncPending(false);
      });
  };

  const scheduleSync = (cart) => {
    if (cart && typeof cart === 'object') {
      latestCartState = cart;
    }

    if (syncTimer) {
      window.clearTimeout(syncTimer);
    }

    setSyncPending(true);
    syncTimer = window.setTimeout(runSync, 75);
  };

  // EVENT: Theme cart updates emit these custom events already, so the promo engine hooks into the same lifecycle instead of replacing the existing AJAX cart flow.
  document.addEventListener('theme:cart:change', (event) => {
    if (dispatchingPromoCartEvents || (event.detail && event.detail.source === 'free-gift-promotions')) {
      if (event.detail && event.detail.cart) {
        latestCartState = event.detail.cart;
        renderPromoMessage(latestCartState);
      }
      return;
    }

    scheduleSync(event.detail && event.detail.cart);
  });

  document.addEventListener('theme:cart:reload', (event) => {
    if (dispatchingPromoCartEvents || (event.detail && event.detail.source === 'free-gift-promotions')) return;

    window.setTimeout(() => {
      renderPromoMessage(latestCartState);
      scheduleSync();
    }, 220);
  });

  document.addEventListener('shopify:section:load', () => {
    readConfig();
    window.setTimeout(() => {
      renderPromoMessage(latestCartState);
      scheduleSync();
    }, 120);
  });

  document.addEventListener('shopify:section:unload', () => {
    readConfig();
    renderPromoMessage(latestCartState);
  });

  // SAFETY: Checkout must wait for gift-line reconciliation so stale or manually added free products cannot be captured by the order.
  document.addEventListener('click', blockCheckoutDuringSync, true);
  document.addEventListener('submit', blockCheckoutSubmitDuringSync, true);

  document.addEventListener('DOMContentLoaded', () => {
    readConfig();
    scheduleSync();
  });
})();
